/* eslint-disable jsx-a11y/img-redundant-alt */
import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import EditorialPageShell from '../components/editorial/EditorialPageShell';
import PageHero from '../components/editorial/PageHero';
import EditorialSection from '../components/editorial/EditorialSection';

import PaymentCheckout from '../components/stripe/PaymentCheckout';
import useFirebaseUser from '../hooks/useFirebaseUser';

const accordionSx = {
  background: 'var(--paper)',
  borderRadius: 0,
  boxShadow: 'none',
  border: '1px solid rgba(36,34,88,0.15)',
  '&:before': { display: 'none' },
  '&.Mui-expanded': {
    borderColor: 'var(--navy-500)',
  },
};

const summarySx = {
  px: 3,
  py: 1.5,
  '& .MuiAccordionSummary-content': {
    alignItems: 'center',
    gap: 2,
  },
  '& .MuiSvgIcon-root': { color: 'var(--navy-300)' },
};

export default function Payment() {
  const [isLoading, setIsLoading] = useState(true);
  const { user, loading: authLoading } = useFirebaseUser();
  const storedAmount = localStorage.getItem('amount');

  useEffect(() => {
    const t = setTimeout(() => setIsLoading(false), 1500);
    return () => clearTimeout(t);
  }, []);

  // Nobody reaches the card form without a live Firebase session. This used
  // to read the uid cookie, which outlives the session — so an expired
  // member sailed through to checkout and their payment was posted against
  // a uid with nothing behind it.
  //
  // Wait for Firebase to answer before deciding: on a hard load or refresh
  // of this page the session is restored asynchronously, and redirecting
  // during that gap would throw a member off their own checkout.
  if (authLoading) {
    return (
      <EditorialPageShell title="Checkout" description="Complete your gift to KBT.">
        <EditorialSection>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: 'var(--navy-500)' }} />
          </Box>
        </EditorialSection>
      </EditorialPageShell>
    );
  }

  if (!user) return <Navigate to="/donate-now" replace />;

  return (
    <EditorialPageShell title="Checkout" description="Complete your gift to KBT.">
      <PageHero
        kicker="Checkout  ·  KBT"
        meta="Secure payment"
        title="Select a"
        titleAccent="payment method."
        subtitle={
          storedAmount
            ? `You're about to give $${storedAmount}.00 to Kehilat Bnei Torah. Thank you.`
            : 'A moment from the kehilla to you. Thank you for being part of this.'
        }
        image="/assets/images/kbt/siyum.jpg"
        height="64vh"
      />

      <EditorialSection
        kicker={`Amount  ·  $${storedAmount || '0'}.00`}
        title="Pick the way"
        titleAccent="that's easiest."
        intro="We process card payments through Stripe — fast, encrypted, and PCI-compliant. PayPal and Venmo coming soon."
        align="center"
      >
        <Box sx={{ maxWidth: 760, mx: 'auto' }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: 'var(--navy-500)' }} />
            </Box>
          ) : (
            <Stack spacing={2}>
              <Accordion defaultExpanded sx={accordionSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
                  <Box
                    component="img"
                    src="/assets/images/creditcard.svg"
                    alt="card"
                    sx={{ width: 30, height: 23 }}
                  />
                  <Box
                    sx={{
                      fontFamily: 'var(--font-body)',
                      fontWeight: 600,
                      fontSize: 16,
                      color: 'var(--navy-500)',
                    }}
                  >
                    Credit or debit card
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, pb: 3 }}>
                  <PaymentCheckout />
                </AccordionDetails>
              </Accordion>

              <Accordion sx={accordionSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
                  <Box
                    component="img"
                    src="/assets/images/paypal.svg"
                    alt="paypal"
                    sx={{ width: 30, height: 23 }}
                  />
                  <Box
                    sx={{
                      fontFamily: 'var(--font-body)',
                      fontWeight: 600,
                      fontSize: 16,
                      color: 'var(--navy-500)',
                    }}
                  >
                    PayPal
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, pb: 3 }}>
                  <Box
                    sx={{
                      fontFamily: 'var(--font-body)',
                      fontStyle: 'italic',
                      color: 'var(--navy-300)',
                    }}
                  >
                    Coming soon.
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion sx={accordionSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
                  <Box
                    component="img"
                    src="/assets/images/venmo.png"
                    alt="venmo"
                    sx={{ width: 30, height: 34 }}
                  />
                  <Box
                    sx={{
                      fontFamily: 'var(--font-body)',
                      fontWeight: 600,
                      fontSize: 16,
                      color: 'var(--navy-500)',
                    }}
                  >
                    Venmo
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, pb: 3 }}>
                  <Box
                    sx={{
                      fontFamily: 'var(--font-body)',
                      fontStyle: 'italic',
                      color: 'var(--navy-300)',
                    }}
                  >
                    Coming soon — send directly to @KehilatBneiTorah in the meantime.
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Stack>
          )}
        </Box>
      </EditorialSection>
    </EditorialPageShell>
  );
}
